import React, { useEffect, useState } from 'react';
import { Form, Col, Row } from 'react-bootstrap';
import { useForm, Controller } from 'react-hook-form';
import DatePicker from 'react-datepicker';

import Modal from '../../components/Modal';
import Button from '../../components/Button';
import { CadernoLPMAAssinatura, CadernoLPMADestruicaoDescarte } from '../../models/modelsCadernoLPMA';
import { STRINGS_CADERNOLPMA } from '../../util/cadernoLPMA/string';
import api from '../../services/api';
import AssinaturaComNomesAdicionais, {
	parseNomesEnvolvidosJson,
	stringifyNomesEnvolvidos,
} from './components/AssinaturaComNomesAdicionais';
import MetodoLimpezaSelect from './components/MetodoLimpezaSelect';
import {
	formatarMetodoLimpezaParaJson,
	normalizarMetodoLimpezaParaFormulario,
} from '../../util/cadernoLPMA/metodoLimpeza';

interface DestruicaoDescarteModalProps {
	show: boolean;
	onHide: () => void;
	onSubmit: (info: CadernoLPMADestruicaoDescarte) => void;
	dadosDestruicaoDescarte?: CadernoLPMADestruicaoDescarte | null;
	assinaturasDisponiveis?: CadernoLPMAAssinatura[];
}

const defaultValues: CadernoLPMADestruicaoDescarte = {
	dataInicioDestruicao: null,
	dataTerminoDestruicao: null,
	responsavelDestruicao: '',
	tipoMaterialDestruidoEmbalagens: false,
	tipoMaterialDestruidoSementes: false,
	tipoMaterialDestruidoCapulho: false,
	tipoMaterialDestruidoPlantulas: false,
	tipoMaterialDestruidoOutros: false,
	outrosTipoMaterialDestruido: '',
	numeroAmostras: '',
	pesoTotalAmostras: 0,
	localDestruicaoAreaExperimental: false,
	localDestruicaoAreaDescarte: false,
	localDestruicaoSalaManipulacao: false,
	localDestruicaoOutros: false,
	outrosLocalDestruicao: '',
	numeroAMI: '',
	observacoesLocalDestruicao: '',
	metodoDestruicaoEnterrio: false,
	metodoDestruicaoAutoclavagem: false,
	metodoDestruicaoMoagem: false,
	metodoDestruicaoIncineracao: false,
	metodoDestruicaoIncorporacao: false,
	metodoDestruicaoOutros: false,
	outrosMetodoDestruicao: '',
	observacoesMetodoDestruicao: '',
	usuarioConfirmacao: '',
};

const normalizarNumero = (valor: any) => {
	if (valor === undefined || valor === null || valor === '') return 0;
	const numero = Number(valor);
	return Number.isNaN(numero) ? 0 : numero;
};

type AbaDestruicaoId =
	| 'registro'
	| 'material'
	| 'limpeza';

const ABAS_DESTRUICAO: Array<{ id: AbaDestruicaoId; label: string }> = [
	{ id: 'registro', label: 'Registro' },
	{ id: 'material', label: 'Material' },
	{ id: 'limpeza', label: 'Limpeza' },
];

const formatarDataParaJson = (valor: any) => {
	if (!valor) return null;
	const data = valor instanceof Date ? valor : new Date(valor);
	return Number.isNaN(data.getTime()) ? null : data.toISOString();
};

const urlObterAmiPorNumeroAutorizacaoCibio = (texto: string): string =>
	`api/v1/autorizacoes-cibio/MovimentacaoInterna/obterporfiltro?NumeroAutorizacaoCibio=${encodeURIComponent(texto)}`;

const urlObterAmiPorCodigo = (texto: string): string =>
	`api/v1/autorizacoes-cibio/MovimentacaoInterna/obterporfiltro?Codigo=${encodeURIComponent(texto)}`;

const candidatosCodigoAmi = (raw: string): string[] => {
	const valor = raw.trim();
	if (!valor) return [];
	const candidatos: string[] = [];
	const adicionar = (item: string) => {
		const candidato = item.trim();
		if (candidato && !candidatos.includes(candidato)) candidatos.push(candidato);
	};
	adicionar(valor);
	adicionar(valor.replace(/\//g, '.'));
	adicionar(valor.replace(/\./g, '/'));
	if (!/^AMI\s/i.test(valor)) {
		adicionar(`AMI ${valor}`);
		adicionar(`AMI ${valor.replace(/\//g, '.')}`);
	}
	return candidatos;
};

const formatarAmiComBarra = (valor: string): string => {
	const digitos = (valor || '').replace(/\D/g, '').slice(0, 7);
	if (digitos.length <= 3) return digitos;
	return `${digitos.slice(0, 3)}/${digitos.slice(3)}`;
};

export default function DestruicaoDescarteModal({
	show,
	onHide,
	onSubmit,
	dadosDestruicaoDescarte,
	assinaturasDisponiveis = [],
}: DestruicaoDescarteModalProps) {
	const {
		register,
		handleSubmit,
		control,
		reset,
		getValues,
		setValue,
		watch,
	} = useForm<CadernoLPMADestruicaoDescarte>({
		mode: 'all',
		defaultValues,
		shouldUnregister: false,
	});

	const [quantidadeLinhasLimpeza, setQuantidadeLinhasLimpeza] = useState<number>(1);
	const [abaDestruicao, setAbaDestruicao] = useState<AbaDestruicaoId>('registro');
	const [loadingAmiLink, setLoadingAmiLink] = useState(false);
	const valorNumeroAmi = watch('numeroAMI');

	// Estados para assinatura
	const [assinaturaEnvolvidos, setAssinaturaEnvolvidos] = useState<string | null>(null);
	const [assinaturaUsuario, setAssinaturaUsuario] = useState<string | null>(null);
	const [mostrarAssinatura, setMostrarAssinatura] = useState(false);
	const [nomesAdicionais, setNomesAdicionais] = useState<string[]>([]);

	// Carregar assinatura do usuário
	useEffect(() => {
		const carregarAssinaturaUsuario = async () => {
			try {
				const token = localStorage.getItem('token');
				if (token) {
					const decoded: any = JSON.parse(atob(token.split('.')[1]));
					const username = decoded.unique_name || '';
					
					const { data } = await api.get(`/api/v1/admin/usuarios/assinatura/${username}`);
					if (data && data.assinaturaBase64) {
						setAssinaturaUsuario(data.assinaturaBase64);
					}
				}
			} catch (error) {
				console.error('Erro ao carregar assinatura:', error);
			}
		};

		if (show) {
			carregarAssinaturaUsuario();
			
			// Carregar assinatura salva se existir
			if (dadosDestruicaoDescarte?.assinaturaEnvolvidosBase64) {
				setAssinaturaEnvolvidos(dadosDestruicaoDescarte.assinaturaEnvolvidosBase64);
			} else {
				setAssinaturaEnvolvidos(null);
			}
			setNomesAdicionais(parseNomesEnvolvidosJson(dadosDestruicaoDescarte?.nomesEnvolvidosAdicionaisJson));
		} else {
			// Limpar estados ao fechar
			setAssinaturaEnvolvidos(null);
			setMostrarAssinatura(false);
			setNomesAdicionais([]);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [show, dadosDestruicaoDescarte?.assinaturaEnvolvidosBase64, dadosDestruicaoDescarte?.nomesEnvolvidosAdicionaisJson]);

	useEffect(() => {
		if (!show) {
			reset(defaultValues);
			setQuantidadeLinhasLimpeza(1);
			setAbaDestruicao('registro');
		} else if (dadosDestruicaoDescarte) {
			setAbaDestruicao('registro');
			// Modo edição - popular formulário com dados existentes
			const parseDate = (valor: any) => {
				if (!valor) return null;
				const data = new Date(valor);
				return Number.isNaN(data.getTime()) ? null : data;
			};

			// Parse do JSON de limpeza
			let limpezas: any[] = [];
			try {
				if (dadosDestruicaoDescarte.limpezaDestruicaoDescarteJson) {
					limpezas = JSON.parse(dadosDestruicaoDescarte.limpezaDestruicaoDescarteJson);
				}
			} catch (e) {}

			// Atualiza quantidade de linhas
			setQuantidadeLinhasLimpeza(Math.max(1, limpezas.length));

			// Prepara dados para resetar o formulário
			const dadosParaReset: any = {
				...dadosDestruicaoDescarte,
				dataInicioDestruicao: parseDate(dadosDestruicaoDescarte.dataInicioDestruicao),
				dataTerminoDestruicao: parseDate(dadosDestruicaoDescarte.dataTerminoDestruicao),
			};

			// Popular campos de limpeza
			limpezas.forEach((item: any, index: number) => {
				const metodoLimpeza = normalizarMetodoLimpezaParaFormulario(item.metodo);
				dadosParaReset[`limpezaNome${index}`] = item.nome || '';
				dadosParaReset[`limpezaDataAntes${index}`] = parseDate(item.dataAntes);
				dadosParaReset[`limpezaDataDepois${index}`] = parseDate(item.dataDepois);
				dadosParaReset[`limpezaMetodo${index}`] = metodoLimpeza.metodo;
				dadosParaReset[`limpezaMetodoOutros${index}`] = metodoLimpeza.outros;
				dadosParaReset[`limpezaResponsavel${index}`] = item.responsavel || '';
			});

			reset(dadosParaReset);
		}
	}, [show, dadosDestruicaoDescarte, reset]);

	const adicionarLinhaLimpeza = () => setQuantidadeLinhasLimpeza((prev) => prev + 1);

	const removerLinhaLimpeza = (index: number) => {
		if (quantidadeLinhasLimpeza === 1) return;
		setQuantidadeLinhasLimpeza((prev) => prev - 1);
		setValue(`limpezaNome${index}` as any, undefined);
		setValue(`limpezaDataAntes${index}` as any, null);
		setValue(`limpezaDataDepois${index}` as any, null);
		setValue(`limpezaMetodo${index}` as any, undefined);
		setValue(`limpezaMetodoOutros${index}` as any, undefined);
		setValue(`limpezaResponsavel${index}` as any, undefined);
	};

	const renderizarData = (nome: keyof CadernoLPMADestruicaoDescarte) => (
		<Controller
			control={control}
			name={nome as any}
			render={({ onChange, value }) => (
				<DatePicker
					selected={value || null}
					onChange={onChange}
					className="input-custom-to-date"
					locale="pt-BR"
					dateFormat="dd/MM/yyyy"
					placeholderText="Selecionar a data"
				/>
			)}
		/>
	);

	const tentarObterAmiPorIdNumerico = async (valorBruto: string): Promise<number | null> => {
		const valor = valorBruto.trim();
		if (!/^\d+$/.test(valor)) return null;
		const id = parseInt(valor, 10);
		if (!Number.isFinite(id) || id <= 0) return null;
		try {
			const { data, status } = await api.get<{ id?: number }>(
				`api/v1/autorizacoes-cibio/MovimentacaoInterna/${id}`,
			);
			if (status === 200 && typeof data?.id === 'number') return data.id;
		} catch {
		}
		return null;
	};

	const abrirAmiDigitada = async (valorBruto: string) => {
		const valor = (valorBruto || '').trim();
		if (!valor) return;
		setLoadingAmiLink(true);
		try {
			let id = await tentarObterAmiPorIdNumerico(valor);
			if (!id) {
				try {
					const { data } = await api.get(urlObterAmiPorNumeroAutorizacaoCibio(valor));
					id = Array.isArray(data) ? data[0]?.id : data?.id;
				} catch {
				}
			}
			if (!id) {
				for (const candidato of candidatosCodigoAmi(valor)) {
					try {
						const { data } = await api.get(urlObterAmiPorCodigo(candidato));
						id = Array.isArray(data) ? data[0]?.id : data?.id;
						if (id) break;
					} catch {
					}
				}
			}
			if (id) {
				window.open(`/autorizacoes/visualizar/ami?id=${id}`, '_blank');
			} else {
				alert(`AMI ${valor} não encontrada no sistema.`);
			}
		} finally {
			setLoadingAmiLink(false);
		}
	};

	const handleSubmitInterno = (dados: CadernoLPMADestruicaoDescarte) => {
		const valores = getValues() as Record<string, any>;
		const dadosNormalizados: Record<string, any> = {
			...dados,
			numeroAmostras: normalizarNumero(dados.numeroAmostras),
			pesoTotalAmostras: normalizarNumero(dados.pesoTotalAmostras),
		};

		const limpezas: any[] = [];
		for (let i = 0; i < quantidadeLinhasLimpeza; i++) {
			const nome = valores[`limpezaNome${i}`];
			const dataAntes = valores[`limpezaDataAntes${i}`];
			const dataDepois = valores[`limpezaDataDepois${i}`];
			const metodo = formatarMetodoLimpezaParaJson(
				valores[`limpezaMetodo${i}`],
				valores[`limpezaMetodoOutros${i}`],
			);
			const responsavel = valores[`limpezaResponsavel${i}`];

			if (nome || dataAntes || dataDepois || metodo || responsavel) {
				limpezas.push({
					nome,
					dataAntes: formatarDataParaJson(dataAntes),
					dataDepois: formatarDataParaJson(dataDepois),
					metodo,
					responsavel,
				});
			}

			delete dadosNormalizados[`limpezaNome${i}`];
			delete dadosNormalizados[`limpezaDataAntes${i}`];
			delete dadosNormalizados[`limpezaDataDepois${i}`];
			delete dadosNormalizados[`limpezaMetodo${i}`];
			delete dadosNormalizados[`limpezaMetodoOutros${i}`];
			delete dadosNormalizados[`limpezaResponsavel${i}`];
		}

		dadosNormalizados.limpezaDestruicaoDescarteJson = limpezas.length ? JSON.stringify(limpezas) : null;

		// Adicionar assinatura aos dados
		const dadosComAssinatura = {
			...dadosNormalizados,
			assinaturaEnvolvidosBase64: assinaturaEnvolvidos || null,
			nomesEnvolvidosAdicionaisJson: stringifyNomesEnvolvidos(nomesAdicionais),
		};

		onSubmit(dadosComAssinatura as CadernoLPMADestruicaoDescarte);
		onHide();
	};

	return (
		<Modal show={show} onHide={onHide} title={STRINGS_CADERNOLPMA.DESTRUICAO_MODAL_TITLE} bodyClassName="caderno-lpma-modal-body">
			<Form onSubmit={handleSubmit(handleSubmitInterno)} autoComplete="off">
				<Form.Row className="row-custom">
					<Form.Group as={Col} md={12}>
						<div className="button-position-caderno" style={{ justifyContent: 'flex-start', gap: '8px', flexWrap: 'wrap' }}>
							{ABAS_DESTRUICAO.map((aba) => (
								<Button
									key={aba.id}
									title={aba.label}
									contained={abaDestruicao === aba.id}
									className={abaDestruicao === aba.id ? 'active' : ''}
									type="button"
									onClick={() => setAbaDestruicao(aba.id)}
								/>
							))}
						</div>
					</Form.Group>
				</Form.Row>

				{abaDestruicao === 'registro' && (
					<React.Fragment>
				<Form.Row className="row-custom">
					<Form.Group as={Col} md={12}>
						<Form.Label>
							<h5>Registro dos envolvidos e das datas inicial/final de realização das atividades abaixo descritas</h5>
						</Form.Label>
					</Form.Group>
				</Form.Row>

				<Row className="row-custom">
					<Col md={3}>
						<Form.Group>
							<Form.Label>Data inicial</Form.Label>
							{renderizarData('dataInicioDestruicao')}
						</Form.Group>
					</Col>
					<Col md={3}>
						<Form.Group>
							<Form.Label>Data final</Form.Label>
							{renderizarData('dataTerminoDestruicao')}
						</Form.Group>
					</Col>
				</Row>

				<AssinaturaComNomesAdicionais
					label="assinatura"
					assinaturaUsuario={assinaturaUsuario}
					assinaturaBase64={assinaturaEnvolvidos}
					onAssinaturaBase64Change={setAssinaturaEnvolvidos}
					nomesAdicionais={nomesAdicionais}
					onNomesAdicionaisChange={setNomesAdicionais}
					mostrarAssinatura={mostrarAssinatura}
					onMostrarAssinaturaChange={setMostrarAssinatura}
					assinaturasDisponiveis={assinaturasDisponiveis}
				/>

				<hr />
					</React.Fragment>
				)}

				{abaDestruicao === 'material' && (
					<React.Fragment>
				<Form.Row className="row-custom">
					<Form.Group as={Col} md={12}>
						<Form.Label>
							<h5>Formulário de registro – destruição/desvitalização e descarte</h5>
						</Form.Label>
					</Form.Group>
				</Form.Row>

				<Form.Row className="row-custom">
					<Form.Group as={Col} md={12}>
						<Form.Label>Tipo de material destruído/descartado</Form.Label>
						<div className="d-flex flex-column">
							<Form.Check type="checkbox" ref={register} name="tipoMaterialDestruidoEmbalagens" label="Embalagens/envelopes" />
							<Form.Check type="checkbox" ref={register} name="tipoMaterialDestruidoSementes" label="Sementes/Grãos" />
							<Form.Check type="checkbox" ref={register} name="tipoMaterialDestruidoCapulho" label="Capulho/fibra/caroço" />
							<Form.Check type="checkbox" ref={register} name="tipoMaterialDestruidoPlantulas" label="Plântulas/Plantas" />
							<Form.Check type="checkbox" ref={register} name="tipoMaterialDestruidoOutros" label="*Outros (especificar)" />
						</div>
						<Form.Control
							className="mt-2"
							ref={register}
							name="outrosTipoMaterialDestruido"
							placeholder="Descreva o material adicional destruído"
						/>
					</Form.Group>
				</Form.Row>

				<Row className="row-custom">
					<Col md={4}>
						<Form.Group>
							<Form.Label>Número de amostras</Form.Label>
							<Form.Control ref={register} name="numeroAmostras" type="number" placeholder="Ex.: 5" />
						</Form.Group>
					</Col>
					<Col md={4}>
						<Form.Group>
							<Form.Label>Peso TOTAL das amostras (kg)</Form.Label>
							<Form.Control ref={register} name="pesoTotalAmostras" type="number" step="0.01" placeholder="Ex.: 1,50" />
						</Form.Group>
					</Col>
				</Row>

				<Form.Row className="row-custom">
					<Form.Group as={Col} md={12}>
						<Form.Label>Local da destruição</Form.Label>
						<div className="d-flex flex-column">
							<Form.Check type="checkbox" ref={register} name="localDestruicaoAreaExperimental" label="Área experimental" />
							<Form.Check type="checkbox" ref={register} name="localDestruicaoAreaDescarte" label="Área de descarte" />
							<Form.Check type="checkbox" ref={register} name="localDestruicaoSalaManipulacao" label="Sala de Manipulação/OGM" />
							<Form.Check type="checkbox" ref={register} name="localDestruicaoOutros" label="Outros (ex.: Caldeira – especificar)" />
						</div>
						<Form.Control
							className="mt-2"
							ref={register}
							name="outrosLocalDestruicao"
							placeholder="Detalhe o local quando assinalar a opção 'Outros'"
						/>
					</Form.Group>
				</Form.Row>

				<Form.Row className="row-custom">
					<Form.Group as={Col} md={4}>
						<Form.Label>N.&ordm; da AMI</Form.Label>
						<Controller
							control={control}
							name="numeroAMI"
							render={({ onChange, value }) => (
								<Form.Control
									value={value || ''}
									placeholder="Informe o número da AMI"
									onChange={(e) => onChange(formatarAmiComBarra(e.target.value))}
								/>
							)}
						/>
						{(valorNumeroAmi || '').trim() ? (
							<button
								type="button"
								className="btn btn-link p-0 mt-1"
								disabled={loadingAmiLink}
								onClick={() => void abrirAmiDigitada(valorNumeroAmi || '')}
							>
								{loadingAmiLink
									? 'Buscando AMI...'
									: `Visualizar AMI ${(valorNumeroAmi || '').trim()}`}
							</button>
						) : null}
					</Form.Group>
				</Form.Row>

				<Form.Row className="row-custom">
					<Form.Group as={Col} md={12}>
						<Form.Label>Observações</Form.Label>
						<Form.Control
							as="textarea"
							rows={2}
							ref={register}
							name="observacoesLocalDestruicao"
							placeholder="Registre informações complementares sobre o local selecionado"
						/>
					</Form.Group>
				</Form.Row>

				<Form.Row className="row-custom">
					<Form.Group as={Col} md={12}>
						<Form.Label>Método de destruição/descarte</Form.Label>
						<div className="d-flex flex-column">
							<Form.Check type="checkbox" ref={register} name="metodoDestruicaoEnterrio" label="Enterrio profundo" />
							<Form.Check type="checkbox" ref={register} name="metodoDestruicaoAutoclavagem" label="Autoclavagem" />
							<Form.Check type="checkbox" ref={register} name="metodoDestruicaoMoagem" label="Moagem" />
							<Form.Check type="checkbox" ref={register} name="metodoDestruicaoIncineracao" label="Incineração" />
							<Form.Check type="checkbox" ref={register} name="metodoDestruicaoIncorporacao" label="Incorporação/grade" />
							<Form.Check type="checkbox" ref={register} name="metodoDestruicaoOutros" label="Outros (especificar)" />
						</div>
						<Form.Control
							className="mt-2"
							ref={register}
							name="outrosMetodoDestruicao"
							placeholder="Informe o método quando assinalar a opção 'Outros'"
						/>
					</Form.Group>
				</Form.Row>

				<Form.Row className="row-custom">
					<Form.Group as={Col} md={12}>
						<Form.Label>Observações sobre o método</Form.Label>
						<Form.Control
							as="textarea"
							rows={2}
							ref={register}
							name="observacoesMetodoDestruicao"
							placeholder="Inclua detalhes adicionais sobre a execução do método adotado"
						/>
					</Form.Group>
				</Form.Row>

				<hr />
					</React.Fragment>
				)}

				{abaDestruicao === 'limpeza' && (
					<React.Fragment>
				<Form.Row className="row-custom">
					<Form.Group as={Col} md={12}>
						<Form.Label>
							<h5>Formulário de limpeza de maquinários/equipamentos utilizados na destruição e descarte</h5>
						</Form.Label>
					</Form.Group>
				</Form.Row>

				<Row className="row-custom font-weight-bold">
					<Col md={4}>Nome e código do maquinário/equipamento</Col>
					<Col md={2}>Datas de limpeza – Antes do uso</Col>
					<Col md={2}>Datas de limpeza – Após o uso</Col>
					<Col md={2}>Método</Col>
					<Col md={2}>Responsável</Col>
				</Row>

				{Array.from({ length: quantidadeLinhasLimpeza }).map((_, index) => {
					const metodoSelecionado = watch(`limpezaMetodo${index}` as any);

					return (
						<Row className="row-custom align-items-end" key={`limpeza-destruicao-${index}`}>
							<Col md={4}>
								<Form.Control ref={register} name={`limpezaNome${index}`} placeholder="Maquinário / equipamento" />
							</Col>
							<Col md={2}>
								<Controller
									control={control}
									name={`limpezaDataAntes${index}` as any}
									render={({ onChange, value }) => (
										<DatePicker
											selected={value || null}
											onChange={onChange}
											className="input-custom-to-date"
											locale="pt-BR"
											dateFormat="dd/MM/yyyy"
											placeholderText="Selecionar a data"
										/>
									)}
								/>
							</Col>
							<Col md={2}>
								<Controller
									control={control}
									name={`limpezaDataDepois${index}` as any}
									render={({ onChange, value }) => (
										<DatePicker
											selected={value || null}
											onChange={onChange}
											className="input-custom-to-date"
											locale="pt-BR"
											dateFormat="dd/MM/yyyy"
											placeholderText="Selecionar a data"
										/>
									)}
								/>
							</Col>
							<Col md={2}>
								<MetodoLimpezaSelect
									register={register}
									name={`limpezaMetodo${index}`}
									nameOutros={`limpezaMetodoOutros${index}`}
									metodoSelecionado={metodoSelecionado}
								/>
							</Col>
							<Col md={2}>
								<Form.Control ref={register} name={`limpezaResponsavel${index}`} placeholder="Responsável" />
							</Col>
							<Col md="auto" className="d-flex justify-content-end">
								{quantidadeLinhasLimpeza > 1 && (
									<Button title="Remover" contained={false} type="button" onClick={() => removerLinhaLimpeza(index)} />
								)}
							</Col>
						</Row>
					);
				})}

				<div className="button-position" style={{ justifyContent: 'flex-start' }}>
					<Button title="Adicionar equipamento" contained={false} type="button" onClick={adicionarLinhaLimpeza} />
				</div>
					</React.Fragment>
				)}

				<div className="button-position">
					<Button title="Cancelar" contained={false} type="button" onClick={onHide} />
					<Button title="Salvar" contained={true} type="submit" />
				</div>
			</Form>
		</Modal>
	);
}

